import { useInfiniteQuery, QueryKey, UseInfiniteQueryResult, UseInfiniteQueryOptions } from '@tanstack/react-query'

import { Success, Fail, ExtendedError } from 'store/types'
import { FETCHING_STATUS } from 'utils/constants'

export type AppInfiniteQueryOptions<R> = UseInfiniteQueryOptions<R, ExtendedError>

type UseAppInfiniteQuery = <R>(
  queryKey: QueryKey,
  queryFn: ({ pageParam }: { pageParam: number }) => Promise<Fail | Success<R>>,
  options?: AppInfiniteQueryOptions<R>,
) => UseInfiniteQueryResult<R, ExtendedError>

export const useAppInfiniteQuery: UseAppInfiniteQuery = (queryKey, queryFn, options) =>
  useInfiniteQuery({
    ...options,
    queryKey,
    queryFn: async ({ pageParam }) => {
      const response = await queryFn({ pageParam })
      if (response.status === FETCHING_STATUS.success) {
        return response.data
      }
      throw response.error
    },
  })

type AppInfiniteQueryMap = <R, M>(
  queryKey: QueryKey,
  queryFn: ({ pageParam }: { pageParam?: number | null }) => Promise<Fail | Success<R>>,
  mapFn: (original: R) => M,
  options?: AppInfiniteQueryOptions<M>,
) => UseInfiniteQueryResult<M, ExtendedError>

export const useAppInfiniteQueryMap: AppInfiniteQueryMap = (queryKey, queryFn, mapFn, options) => {
  const queryWithMap = async ({ pageParam = null }) => {
    const response = await queryFn({ pageParam })
    if (response.status === FETCHING_STATUS.success) {
      const mapped = mapFn(response.data)
      return mapped
    }
    throw response.error
  }
  return useInfiniteQuery({
    ...options,
    queryKey,
    queryFn: queryWithMap,
  })
}

export default useAppInfiniteQuery
